<template>
  <v-menu v-model="menuOpen" location="bottom end" offset="8">
    <template #activator="{ props: menuProps }">
      <v-btn v-bind="menuProps" :class="[
        'language-selector',
        `language-selector--${mode}`
      ]" variant="text" size="small">
        <v-icon size="18" class="mr-2">
          mdi-web
        </v-icon>

        <span class="language-code">
          {{ selectedLanguage.displayCode }}
        </span>

        <v-icon size="18" class="ml-1">
          mdi-chevron-down
        </v-icon>
      </v-btn>
    </template>

    <v-card :class="[
      'language-card',
      `language-card--${mode}`
    ]" elevation="6">
      <v-list density="compact" :class="[
        'language-menu',
        `language-menu--${mode}`
      ]">
        <v-list-item v-for="language in languages" :key="language.code" class="language-item" :class="{
          'selected-language': language.code === normalizedModelValue
        }" @click="changeLanguage(language.code)">
          <v-list-item-title>
            {{ language.label }}
          </v-list-item-title>

          <template #append>
            <v-icon v-if="language.code === normalizedModelValue" size="18" color="primary">
              mdi-check
            </v-icon>
          </template>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: 'en'
  },
  mode: {
    type: String,
    default: 'dark',
    validator: value => ['dark', 'light'].includes(value)
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const menuOpen = ref(false)

const languages = [
  {
    code: 'en',
    displayCode: 'EN',
    label: 'English'
  },
  {
    code: 'ja',
    displayCode: 'JA',
    label: '日本語'
  }
]

const normalizedModelValue = computed(() => {
  return props.modelValue?.toLowerCase() || 'en'
})

const selectedLanguage = computed(() => {
  return (
    languages.find(language => language.code === normalizedModelValue.value) ||
    languages.find(language => language.code === 'en')
  )
})

function changeLanguage(languageCode) {
  emit('update:modelValue', languageCode)
  emit('change', languageCode)
  menuOpen.value = false
}
</script>

<style scoped>
.language-selector {
  border-radius: 8px;
  padding: 0 10px;
  min-width: 76px;
  height: 36px;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.language-code {
  font-size: 14px;
  line-height: 1;
}

.language-selector--dark {
  color: #ffffff;
  border: 1px solid #ffffff;
}

.language-selector--dark:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.language-menu--dark {
  background-color: #070f26;
  color: white;
}

.language-selector--light {
  color: #070f26;
  border: 1px solid rgba(7, 15, 38, 0.2);
  background-color: rgba(255, 255, 255, 0.85);
}

.language-selector--light:hover {
  background-color: #f2f6fb;
}

.language-menu--light {
  background-color: white;
  color: #070f26;
}

.language-item {
  cursor: pointer;
  min-height: 42px;
}

.language-menu--dark .language-item {
  color: rgb(255, 255, 255);
}

.language-menu--light .language-item {
  color: #070f26;
}

.language-item:hover {
  background-color: rgba(108, 170, 245, 0.18);
}

.selected-language {
  background-color: rgba(108, 170, 245, 0.28);
}

.language-card {
  border-radius: 8px !important;
  overflow: hidden;
}

.language-card--light {
  border: 1px solid rgba(7, 15, 38, 0.08);
}

.language-card--dark {
  border: 1px solid rgba(255, 255, 255, 0.08);
}
</style>